import { useCallback, useEffect, useState } from "react";
import { Plan } from "../models/SubscriptionModels";
import { getPublicPlansAnonymousAsync } from "../api/SubscriptionApi";

interface UsePublicPlansResult {
  plans: Plan[];
  loading: boolean;
  error: string | null;
  reload: () => void;
}

/**
 * Loads publicly visible subscription plans for the marketing pricing page.
 * Anonymous call — safe to use before login.
 */
export const usePublicPlans = (): UsePublicPlansResult => {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getPublicPlansAnonymousAsync();
      if (response.success && Array.isArray(response.result)) {
        const visible = response.result
          .filter((plan) => plan.isActive !== false && plan.isPublic !== false)
          .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
        setPlans(visible);
      } else {
        setError(response.message || "We couldn't load the plans right now.");
      }
    } catch {
      setError(
        "We couldn't load the plans right now. Please check your connection and try again.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return { plans, loading, error, reload: load };
};
